import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon, MenuIcon, XIcon, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant45() {
  const {
    language,
    t,
    getPath
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleLanguageChange = (lang: 'en' | 'ru' | 'ua') => {
    const currentPath = window.location.pathname;
    let newPath = currentPath;
    if (currentPath.startsWith('/ru')) newPath = currentPath.substring(3) || '/';else if (currentPath.startsWith('/ua') || currentPath.startsWith('/uk')) newPath = currentPath.substring(3) || '/';
    if (lang === 'ru') newPath = `/ru${newPath === '/' ? '' : newPath}`;else if (lang === 'ua') newPath = `/ua${newPath === '/' ? '' : newPath}`;
    window.location.href = newPath;
  };
  const content = {
    ru: {
      title1: 'Сделаем сайт для вас бесплатно.',
      title2: `Обслуживание — ${price}/мес.`,
      sub: `Мы делаем сайт за свой счёт. Вы смотрите, просите правки, утверждаете. Когда всё нравится — платите ${price} в месяц за хостинг и поддержку. Не понравится — просто уходите. Без оплаты.`,
      cta: 'Оставить заявку',
      p1: 'Дизайн и код за наш счет',
      p2: 'Оплата только за результат'
    },
    ua: {
      title1: 'Зробимо сайт для вас безкоштовно.',
      title2: `Обслуговування — ${price}/міс.`,
      sub: `Ми робимо сайт за свій рахунок. Ви дивитеся, просите правки, затверджуєте. Коли все подобається — платите ${price} на місяць за хостинг та підтримку. Не сподобається — просто йдете. Без оплати.`,
      cta: 'Залишити заявку',
      p1: 'Дизайн та код за наш рахунок',
      p2: 'Оплата лише за результат'
    },
    en: {
      title1: 'We build your website for free.',
      title2: `Maintenance — ${price}/mo.`,
      sub: `We build the site at our expense. You review, request edits, and approve. When you love it, you pay ${price}/month for hosting and support. Don't like it? Just walk away. No charge.`,
      cta: 'Submit request',
      p1: 'Design & code at our expense',
      p2: 'Pay only for the result'
    }
  }[language] || content.ru;
  return <>
      <section className="relative w-full h-screen min-h-[800px] flex flex-col overflow-hidden bg-black" data-id="element-739">
        {/* Full Screen Video Background */}
        <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-50" data-id="element-740" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" data-id="element-741" />

        {/* Transparent Header Inside Hero */}
        <header className="relative z-50 w-full pt-6 pb-4 px-4 sm:px-6 lg:px-8" data-id="element-742">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-743">
            <a href={getPath('')} className="flex items-center gap-1 text-white" data-id="element-744">
              <span className="text-2xl font-bold tracking-tight" data-id="element-745">J</span>
              <span className="w-2 h-2 rounded-full bg-blue-500" data-id="element-746" />
            </a>

            <div className="hidden md:flex items-center gap-8" data-id="element-747">
              <a href="#how-it-works" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-748">
                {t('nav.howItWorks')}
              </a>
              <a href="#services" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-749">
                {t('nav.pricing')}
              </a>
              <a href="#faq" className="text-sm font-semibold text-white/90 hover:text-white transition-colors" data-id="element-750">
                {t('nav.faq')}
              </a>
            </div>

            <div className="flex items-center gap-4" data-id="element-751">
              <div className="hidden md:block relative" data-id="element-752">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-all backdrop-blur-md border border-white/10" data-id="element-753">
                  <span className="uppercase" data-id="element-754">{language}</span>
                  <ChevronDownIcon className="w-4 h-4" data-id="element-755" />
                </button>
                <AnimatePresence data-id="element-756">
                  {isLangMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full mt-2 right-0 rounded-lg bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden shadow-2xl min-w-[80px]" data-id="element-757">
                      {['en', 'ru', 'ua'].map(lang => <button key={lang} onClick={() => handleLanguageChange(lang as any)} className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${language === lang ? 'bg-blue-600 text-white' : 'text-white hover:bg-white/10'}`} data-id="element-758">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button className="md:hidden p-2 text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-759">
                {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-760" /> : <MenuIcon className="w-6 h-6" data-id="element-761" />}
              </button>
            </div>
          </div>
        </header>

        {/* Split Content Inside Video */}
        <div className="relative z-10 flex-1 flex items-center px-4 sm:px-6 lg:px-8" data-id="element-762">
          <div className="max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center justify-between gap-12" data-id="element-763">
            {/* Left - Massive Typography */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="w-full lg:w-3/5" data-id="element-764">
              <h1 className="mb-6 text-5xl md:text-6xl lg:text-[4.5rem] font-black leading-[1.1] text-white tracking-tight" data-id="element-765">
                {content.title1}
              </h1>
              <p className="text-lg md:text-xl text-gray-300 font-medium leading-relaxed max-w-xl" data-id="element-766">
                {content.sub}
              </p>
            </motion.div>

            {/* Right - Glass Offer Card */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="w-full lg:w-2/5" data-id="element-767">
              <div className="bg-white/10 backdrop-blur-2xl border border-white/20 p-8 rounded-[2rem] shadow-2xl" data-id="element-768">
                <div className="text-blue-400 font-bold text-sm uppercase tracking-widest mb-2" data-id="element-769">
                  Ваш тариф
                </div>
                <h2 className="text-4xl font-black text-white mb-6" data-id="element-770">
                  {content.title2}
                </h2>

                <div className="space-y-4 mb-8" data-id="element-771">
                  <div className="flex items-center gap-3 text-white/90" data-id="element-772">
                    <CheckCircle2 className="w-6 h-6 text-blue-400" data-id="element-773" />
                    <span className="font-medium" data-id="element-774">{content.p1}</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90" data-id="element-775">
                    <CheckCircle2 className="w-6 h-6 text-blue-400" data-id="element-776" />
                    <span className="font-medium" data-id="element-777">{content.p2}</span>
                  </div>
                </div>

                <button onClick={() => setIsPopupOpen(true)} className="w-full group flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-bold text-lg transition-all hover:scale-105 bg-blue-600 text-white shadow-lg shadow-blue-600/30" data-id="element-778">
                  {content.cta}
                  <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-779" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-780" />
    </>;
}